/**
 * Loading Test Component - Preview page for loading animations
 * 
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Last Updated: November 2025
 * Version: 1.0.0
 */

import React, { useState } from 'react';
import LoadingSpinner from './LoadingSpinner';
import SEO from './SEO';
import './LoadingTest.css';

const LoadingTest = () => {
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [selectedSize, setSelectedSize] = useState('medium');
  const [customMessage, setCustomMessage] = useState('Loading...');

  return (
    <>
      <SEO 
        title="Loading Animation Test"
        description="Test page for loading animations"
      />
      
      <div className="loading-test-container">
        <div className="loading-test-header">
          <h1>Loading Animation Preview</h1>
          <p>Test and preview different loading animation variations</p>
        </div>

        {/* Controls */}
        <div className="loading-test-controls">
          <div className="control-group">
            <label>Size:</label>
            <div className="button-group">
              <button 
                className={selectedSize === 'small' ? 'active' : ''}
                onClick={() => setSelectedSize('small')}
              >
                Small
              </button>
              <button 
                className={selectedSize === 'medium' ? 'active' : ''}
                onClick={() => setSelectedSize('medium')}
              >
                Medium
              </button>
              <button 
                className={selectedSize === 'large' ? 'active' : ''}
                onClick={() => setSelectedSize('large')}
              >
                Large
              </button>
            </div>
          </div>

          <div className="control-group">
            <label htmlFor="message-input">Message:</label>
            <input
              id="message-input"
              type="text"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Enter loading message"
            />
          </div>

          <div className="control-group">
            <button 
              className="fullscreen-btn"
              onClick={() => setShowFullscreen(!showFullscreen)}
            >
              {showFullscreen ? 'Hide' : 'Show'} Fullscreen Version
            </button>
          </div>
        </div>

        {/* Preview Areas */}
        <div className="loading-test-previews">
          {/* Light Background Preview */}
          <div className="preview-section light-bg">
            <h3>Light Background</h3>
            <div className="preview-box">
              <LoadingSpinner message={customMessage} size={selectedSize} />
            </div>
          </div>

          {/* Dark Background Preview */}
          <div className="preview-section dark-bg">
            <h3>Dark Background (Default)</h3>
            <div className="preview-box">
              <LoadingSpinner message={customMessage} size={selectedSize} />
            </div>
          </div>

          {/* In Context Preview */}
          <div className="preview-section context-preview">
            <h3>In Page Context</h3>
            <div className="preview-box context-box">
              <div className="mock-header">Page Header</div>
              <LoadingSpinner message={customMessage} size={selectedSize} />
              <div className="mock-footer">Page Footer</div>
            </div>
          </div>
        </div>

        {/* Usage Examples */}
        <div className="usage-examples">
          <h2>Usage Examples</h2>
          <div className="code-examples">
            <div className="code-block">
              <h3>Basic Usage</h3>
              <pre><code>{`import LoadingSpinner from './LoadingSpinner';

// In your component
{loading && <LoadingSpinner />}`}</code></pre>
            </div>

            <div className="code-block">
              <h3>With Custom Message</h3>
              <pre><code>{`<LoadingSpinner message="Loading articles..." />`}</code></pre>
            </div>

            <div className="code-block">
              <h3>Different Sizes</h3>
              <pre><code>{`<LoadingSpinner size="small" />
<LoadingSpinner size="medium" />
<LoadingSpinner size="large" />`}</code></pre>
            </div>

            <div className="code-block">
              <h3>Fullscreen Overlay</h3>
              <pre><code>{`<div className="loading-spinner-fullscreen">
  <LoadingSpinner message="Processing..." />
</div>`}</code></pre>
            </div>
          </div>
        </div>

        {/* Animation Details */}
        <div className="animation-details">
          <h2>Animation Details</h2>
          <ul>
            <li>Uses custom fiddler logo from Cranford Publications</li>
            <li>Smooth pulse and gentle rotation animation</li>
            <li>Responsive design adapts to screen size</li>
            <li>Customizable size (small, medium, large)</li>
            <li>Optional loading message</li>
            <li>Works on light and dark backgrounds</li>
          </ul>
        </div>
      </div>

      {/* Fullscreen Demo */}
      {showFullscreen && (
        <div className="loading-spinner-fullscreen">
          <LoadingSpinner message={customMessage} size={selectedSize} />
        </div>
      )}
    </>
  );
};

export default LoadingTest;

